import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import { CircularProgress } from '@material-ui/core';
import { useLocation } from 'react-router-dom';

import CustomButton from 'components/Button';
import { ICustomTheme } from 'interfaces';
import { SubscribeAPI } from 'services/subscribe.api';

const Container = styled.div(({ theme }: { theme: ICustomTheme }) => ({
  marginTop: theme.spacing(4),
}));

const Title = styled(Typography)(({ theme }: { theme: ICustomTheme }) => ({
  textAlign: 'center',
  fontWeight: theme.typography.fontWeightBold,
  color: theme.colors.grey.dark,
}));

const Message = styled(Typography)(({ theme }: { theme: ICustomTheme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(2),
  color: theme.colors.grey.dark,
}));

const ButtonContainer = styled.div(({ theme }: { theme: ICustomTheme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(2),
  '& button': {
    width: 330,
    '&:hover svg': {
      color: theme.colors.paletteFromBO.main,
    },
  },
}));

const Spinner = styled(CircularProgress)(
  ({ theme }: { theme: ICustomTheme }) => ({
    marginRight: theme.spacing(1),
    color: theme.colors.white,
  }),
);

export default function InvalidFirstLoginLink() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const resendToken = new URLSearchParams(search).get('resendToken') || '';
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleResend = async () => {
    if (!resendToken || isLoading) return;
    setIsLoading(true);
    try {
      await SubscribeAPI.resendFirstLoginEmail(resendToken);
    } finally {
      setIsLoading(false);
      setIsSent(true);
    }
  };

  return (
    <Container>
      <Title>{t('auth.linkExpired.title')}</Title>
      {isSent ? (
        <Message variant="body2">{t('auth.linkExpired.emailSent')}</Message>
      ) : (
        <>
          <Message variant="body2">{t('auth.linkExpired.message')}</Message>
          <ButtonContainer>
            <CustomButton onClick={handleResend} disabled={isLoading || !resendToken}>
              {isLoading && <Spinner size="2.5rem" />}
              {t('auth.linkExpired.resendButton')}
            </CustomButton>
          </ButtonContainer>
        </>
      )}
    </Container>
  );
}
